import React, { useRef, useState } from "react";
import { X, Upload, Link2, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * MediaSourcePicker — small modal for choosing a video source for a scene
 * or the ad preview tile. User can:
 *   (a) Upload a file from device  → POST /api/uploads/video → we turn the
 *       returned storage_path into a public /api/files/{path} URL
 *   (b) Paste any link (YouTube URL, a hosted MP4, an HLS manifest, etc.)
 *
 * Returns the chosen source via `onSave({ kind: "upload"|"link", url, label })`.
 * Passing `initial={url, label}` pre-fills the modal. `onClear` is optional —
 * shown when initial is set so users can detach the asset.
 */
export default function MediaSourcePicker({ open, onClose, onSave, onClear, title = "Scene media", initial }) {
  const fileRef = useRef(null);
  const [tab, setTab] = useState(initial?.kind === "link" ? "link" : "upload");
  const [linkUrl, setLinkUrl] = useState(initial?.kind === "link" ? (initial.url || "") : "");
  const [linkLabel, setLinkLabel] = useState(initial?.label || "");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  if (!open) return null;

  const uploadFile = async (f) => {
    if (!f) return;
    // 2 GB hard cap (backend stream limit) + friendly warning for slow uploads
    if (f.size > 2 * 1024 * 1024 * 1024) {
      toast.error("File too big — max 2GB per scene clip.");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      // `storage_path` is relative to /api/files/
      const url = `/api/files/${data.storage_path}`;
      onSave?.({ kind: "upload", url, label: f.name });
      toast.success(`Uploaded ${f.name}`, { id: "media-upload" });
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const saveLink = () => {
    const u = (linkUrl || "").trim();
    if (!u) { toast.error("Paste a video or stream URL"); return; }
    if (!/^https?:\/\//.test(u)) { toast.error("Link must start with http:// or https://"); return; }
    onSave?.({ kind: "link", url: u, label: linkLabel || u });
    toast.success("Link saved", { id: "media-link" });
    onClose?.();
  };

  return (
    <div
      data-testid="media-source-picker"
      className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#0B0F16] border border-tv-accent/30 rounded-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-white/10">
          <div className="font-display font-black text-white text-sm">{title}</div>
          <div className="flex-1" />
          <button
            data-testid="media-picker-close"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pt-3">
          <button
            data-testid="media-tab-upload"
            onClick={() => setTab("upload")}
            aria-pressed={tab === "upload"}
            className={`flex-1 flex items-center justify-center gap-1.5 text-[11px] font-bold py-2 rounded-t-lg transition ${
              tab === "upload" ? "bg-white/8 text-white" : "bg-transparent text-white/50 hover:text-white"
            }`}
          >
            <Upload className="w-3.5 h-3.5" /> Upload from device
          </button>
          <button
            data-testid="media-tab-link"
            onClick={() => setTab("link")}
            aria-pressed={tab === "link"}
            className={`flex-1 flex items-center justify-center gap-1.5 text-[11px] font-bold py-2 rounded-t-lg transition ${
              tab === "link" ? "bg-white/8 text-white" : "bg-transparent text-white/50 hover:text-white"
            }`}
          >
            <Link2 className="w-3.5 h-3.5" /> Paste link
          </button>
        </div>

        {/* Body */}
        <div className="p-5 bg-white/[0.04]">
          {tab === "upload" && (
            <div className="space-y-3">
              <input
                ref={fileRef}
                type="file"
                accept="video/*"
                className="hidden"
                data-testid="media-upload-input"
                onChange={(e) => uploadFile(e.target.files?.[0])}
              />
              <button
                data-testid="media-upload-click"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
                className={`w-full flex items-center justify-center gap-2 border-2 border-dashed rounded-xl py-6 transition ${
                  uploading ? "border-tv-accent bg-tv-accent/5 text-white" : "border-white/15 hover:border-tv-accent hover:bg-tv-accent/5 text-white/70 hover:text-white"
                }`}
              >
                <Upload className="w-5 h-5 text-tv-accent" />
                <div className="text-left">
                  <div className="text-sm font-bold">
                    {uploading ? `Uploading… ${progress}%` : "Choose a video file"}
                  </div>
                  <div className="text-[10px] text-white/40">MP4 · MOV · WebM — up to 2GB</div>
                </div>
              </button>
              {uploading && (
                <div className="h-1 bg-white/10 rounded overflow-hidden">
                  <div className="h-full bg-tv-accent transition-all" style={{ width: `${progress}%` }} />
                </div>
              )}
            </div>
          )}

          {tab === "link" && (
            <div className="space-y-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-black text-white/50 mb-1 block">Video URL</span>
                <input
                  type="url"
                  data-testid="media-link-url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com/clip.mp4"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs outline-none focus:border-tv-accent"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider font-black text-white/50 mb-1 block">Label (optional)</span>
                <input
                  type="text"
                  data-testid="media-link-label"
                  value={linkLabel}
                  onChange={(e) => setLinkLabel(e.target.value)}
                  placeholder="Intro clip · March 2026"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs outline-none focus:border-tv-accent"
                />
              </label>
              <button
                data-testid="media-link-save"
                onClick={saveLink}
                className="w-full flex items-center justify-center gap-2 bg-tv-accent hover:bg-yellow-400 text-black font-black text-xs py-2.5 rounded-lg"
              >
                <Check className="w-4 h-4" /> Save link
              </button>
              <div className="text-[10px] text-white/40 italic">
                Direct MP4/WebM links and HLS (.m3u8) manifests play natively. YouTube/TikTok links open in a new tab when hit on stream.
              </div>
            </div>
          )}
        </div>

        {/* Footer — show Clear when an initial source is set */}
        {initial?.url && (
          <div className="flex items-center gap-2 px-5 py-3 border-t border-white/10">
            <button
              data-testid="media-clear"
              onClick={() => { onClear?.(); onClose?.(); }}
              className="flex items-center gap-1.5 text-red-300 hover:text-red-200 text-xs font-bold"
            >
              <Trash2 className="w-3.5 h-3.5" /> Clear media
            </button>
            <div className="flex-1" />
            <div className="text-[10px] text-white/40 truncate max-w-[50%]">Current: {initial.label || initial.url}</div>
          </div>
        )}
      </div>
    </div>
  );
}
